import * as React from "react";
// helper
import { isBrowser } from "@vingle/wtf-sdk/helpers/envChecker";

import styled from "styled-components";

interface IProps {
  content: string;
  onContentChange: (value: string) => void;
  id?: string;
  autoFocus?: boolean;
  className?: string;
  placeholder?: string;
  readOnly?: boolean;
  maxLength?: number;
  onEnter?: () => "handled" | "not-handled";
  disabled?: boolean;
}

const TextArea = styled.textarea`
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;
  hyphens: auto;
  cursor: text;
  outline: none;
`;

interface IState {
  text: string;
}

class ResponsiveTextArea extends React.PureComponent<IProps, IState> {
  public readonly state: IState = {
    text: this.props.content,
  };
  private readonly textareaRef = React.createRef<HTMLTextAreaElement>();

  public componentDidMount() {
    if (isBrowser()) {
      requestAnimationFrame(() => {
        this.dynamicHeightChange();
      });
    }
  }

  public componentDidUpdate(prevProps: IProps) {
    if (prevProps.content !== this.props.content) {
      this.setState({
        text: this.props.content,
      });
    }

    this.dynamicHeightChange();
  }

  public render() {
    const {
      className: addedClassName,
      content,
      placeholder,
      readOnly,
      maxLength,
      onContentChange: _onContentChange,
      onEnter: _onEnter,
      ...props
    } = this.props;

    return (
      <TextArea
        className={addedClassName}
        ref={this.textareaRef}
        placeholder={placeholder}
        onKeyDown={this.onKeyDown}
        onChange={this.onChange}
        maxLength={maxLength}
        value={this.state.text}
        readOnly={Boolean(readOnly)}
        {...props}
      />
    );
  }

  public readonly focus = () => {
    if (this.textareaRef.current) {
      this.textareaRef.current.focus();
    }
  };

  public readonly blur = () => {
    if (this.textareaRef.current) {
      this.textareaRef.current.blur();
    }
  };

  public readonly setValue = (value: string) => {
    this.setState({
      text: value,
    });
  };

  private readonly onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.currentTarget.value;
    this.setState({
      text,
    });
    this.props.onContentChange(text);
    this.dynamicHeightChange();
  };

  private readonly onKeyDown = (
    e: React.KeyboardEvent<HTMLTextAreaElement>,
  ) => {
    if (
      e.keyCode === 13 &&
      this.props.onEnter &&
      this.props.onEnter() === "handled"
    ) {
      // enter
      e.preventDefault();
      return;
    }
  };

  private readonly dynamicHeightChange = () => {
    const node = this.textareaRef.current;
    if (!node) return;
    node.style.height = "1px";
    node.style.height = `${node.scrollHeight}px`;
  };
}

export default ResponsiveTextArea;
